import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays, sub } from "date-fns";
import type { ActionItem } from "@/components/actions/ActionCard";

// Document types that are CRITICAL by nature (show alert even without explicit date)
const CRITICAL_DOC_TYPES = [
  'mise_en_demeure',
  'commandement_payer', 
  'avis_huissier',
  'injonction',
  'convocation',
] as const;

// Document types that are ACTUALLY payable (invoices, bills, charges)
// Receipts are EXCLUDED - they prove payment was made, not that payment is due
const PAYABLE_DOC_TYPES = [
  'invoice',           // Factures à payer uniquement
] as const;

// Maximum days overdue to still show in Actions (beyond this = too late to act)
const MAX_OVERDUE_DAYS = 90;

// Document types that should NEVER be treated as payments
// Even if they have a due_date or amount - they are reference documents
const NON_PAYABLE_DOC_TYPES = [
  'contract',          // Contrats (DCE, marchés)
  'quote',             // Devis
  'attestation',       // Attestations
  'administrative',    // Documents administratifs
  'bank_statement',    // Relevés bancaires (tableaux d'amortissement)
  'payslip',           // Bulletins de salaire
  'tax',               // Avis d'imposition (référence, pas paiement)
  'identity',          // Pièces d'identité
  'insurance',         // Assurances
  'kbis',              // Kbis
  'gray_card',         // Carte grise
  'other',             // Autres
] as const;

// Scoring weights for prioritization
const SCORE_WEIGHTS = {
  fine_active: 100,
  payment_due_7d: 95,
  payment_due_30d: 85,
  expiration_30d: 80,
  expiration_90d: 60,
  freshness_15d: 70,
  freshness_30d: 50,
  critical_doc_type: 90,
};

export interface ActionStats {
  total: number;
  completed: number;
  urgent: number;
  thisWeek: number;
  expired: number;
}

interface UseActionsDataReturn {
  items: ActionItem[];
  stats: ActionStats;
  loading: boolean;
  completedThisMonth: number;
  refetch: () => Promise<void>;
}

export function useActionsData(): UseActionsDataReturn {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ActionItem[]>([]);
  const [completedThisMonth, setCompletedThisMonth] = useState(0);

  const fetchData = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    const today = new Date();
    const in90Days = new Date();
    in90Days.setDate(in90Days.getDate() + 90);
    const in30Days = new Date();
    in30Days.setDate(in30Days.getDate() + 30);

    // Parallel fetch all sources
    const [
      { data: finesData },
      { data: expiringDocsData },
      { data: dueDateDocsData },
      { data: freshnessDocsData },
      { data: manualActionsData },
      { data: demarchesData },
      { count: completedCount },
    ] = await Promise.all([
      // SOURCE 1: Traffic fines (CRITICAL)
      supabase
        .from("traffic_fines")
        .select("*, document:documents!document_id(id, title, file_path, mime_type)")
        .eq("user_id", user.id)
        .in("status", ["received", "pending_designation"])
        .order("payment_deadline", { ascending: true }),

      // SOURCE 2: Documents with expires_at <= 90 days
      supabase
        .from("documents")
        .select("id, title, doc_type, expires_at, file_path, mime_type, expiry_dismissed_at, date_context, action_required, action_label, created_at")
        .eq("user_id", user.id)
        .not("expires_at", "is", null)
        .is("expiry_dismissed_at", null)
        .lte("expires_at", in90Days.toISOString())
        .or("date_context.is.null,date_context.neq.reference")
        .order("expires_at", { ascending: true }),

      // SOURCE 3: Invoices with due_date (SMART WINDOW + UNPAID ONLY)
      // - Only UNPAID invoices (doc_status != 'paid')
      // - Future due dates: show (upcoming payments)
      // - Past due dates: show only if < 90 days overdue (recent forgotten payments)
      // - Past > 90 days: ignore (too late to act)
      supabase
        .from("documents")
        .select("id, title, doc_type, due_date, total_amount, currency, file_path, mime_type, expiry_dismissed_at, issuer_name, doc_status")
        .eq("user_id", user.id)
        .not("due_date", "is", null)
        .is("expiry_dismissed_at", null)
        .in("doc_type", [...PAYABLE_DOC_TYPES])
        .neq("doc_status", "paid") // ONLY UNPAID invoices
        .gte("due_date", sub(today, { days: MAX_OVERDUE_DAYS }).toISOString()) // Not older than 90 days
        .lte("due_date", in30Days.toISOString()) // Up to 30 days in future
        .order("due_date", { ascending: true }),

      // SOURCE 4: Documents with fresh_until <= 30 days (administrative freshness)
      supabase
        .from("documents")
        .select("id, title, doc_type, fresh_until, file_path, mime_type, expiry_dismissed_at, issuer_name")
        .eq("user_id", user.id)
        .not("fresh_until", "is", null)
        .is("expiry_dismissed_at", null)
        .lte("fresh_until", in30Days.toISOString())
        .order("fresh_until", { ascending: true }),

      // Manual actions (user-created, no auto-generated noise)
      supabase
        .from("document_actions")
        .select(`*, document:documents!document_id(id, title, doc_type, file_path, mime_type)`)
        .eq("user_id", user.id)
        .eq("status", "pending")
        .order("due_date", { ascending: true }),

      // Active demarches for badge display
      supabase
        .from("demarches")
        .select("id, title, status, source_document_id")
        .eq("user_id", user.id)
        .in("status", ["pending", "in_progress"])
        .not("source_document_id", "is", null),

      // Completed this month (filtered by current user)
      (() => {
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);
        return supabase
          .from("document_actions")
          .select("*", { count: 'exact', head: true })
          .eq("user_id", user.id)
          .eq("status", "done")
          .gte("updated_at", startOfMonth.toISOString());
      })(),
    ]);

    setCompletedThisMonth(completedCount || 0);

    // Build demarche map
    const docToDemarche = new Map<string, { id: string; title: string; status: string }>();
    (demarchesData || []).forEach(d => {
      if (d.source_document_id) {
        docToDemarche.set(d.source_document_id, { id: d.id, title: d.title, status: d.status || 'pending' });
      }
    });

    // Track seen document IDs to avoid duplicates
    const seenDocIds = new Set<string>();
    const allItems: ActionItem[] = [];

    // Transform FINES (highest priority)
    (finesData || []).forEach(fine => {
      const nextDeadline = getNextFineDeadline(fine);
      const days = nextDeadline ? differenceInDays(new Date(nextDeadline), today) : 0;
      
      allItems.push({
        id: `fine-${fine.id}`,
        type: 'expiring',
        actionType: 'pay',
        title: `Amende ${fine.offense_type === 'speeding' ? 'excès de vitesse' : fine.offense_description || 'à payer'}`,
        documentTitle: fine.document?.title || `${fine.license_plate}`,
        documentId: fine.document_id || fine.id,
        docType: 'traffic_fine',
        priority: 'urgent',
        expiresAt: nextDeadline || undefined,
        daysUntilExpiry: days,
        filePath: fine.document?.file_path,
        mimeType: fine.document?.mime_type,
        fineData: {
          amount: fine.fine_amount,
          reducedAmount: fine.reduced_amount,
          reducedDeadline: fine.reduced_deadline,
          normalDeadline: fine.payment_deadline,
          reference: fine.reference_number,
        },
        score: SCORE_WEIGHTS.fine_active,
      });

      if (fine.document_id) seenDocIds.add(fine.document_id);
    });

    // Transform PAYMENT DUE DATES (invoices, charges)
    (dueDateDocsData || []).filter(doc => !seenDocIds.has(doc.id)).forEach(doc => {
      const days = differenceInDays(new Date(doc.due_date!), today);
      seenDocIds.add(doc.id);
      
      allItems.push({
        id: doc.id,
        type: 'expiring',
        actionType: 'pay',
        title: doc.total_amount 
          ? `Facture ${doc.total_amount}${doc.currency || '€'} à payer`
          : 'Paiement à effectuer',
        documentTitle: doc.title,
        documentId: doc.id,
        docType: doc.doc_type,
        priority: days <= 7 ? 'urgent' : 'high',
        expiresAt: doc.due_date!,
        daysUntilExpiry: days,
        filePath: doc.file_path,
        mimeType: doc.mime_type,
        linkedDemarche: docToDemarche.get(doc.id),
        dateContext: 'deadline',
        score: days <= 7 ? SCORE_WEIGHTS.payment_due_7d : SCORE_WEIGHTS.payment_due_30d,
        showAlreadyPaid: true, // Flag to show "Déjà payé" button
      });
    });

    // Transform EXPIRATIONS
    (expiringDocsData || []).filter(doc => !seenDocIds.has(doc.id)).forEach(doc => {
      const days = differenceInDays(new Date(doc.expires_at!), today);
      seenDocIds.add(doc.id);

      const dateContext = doc.date_context as string | null;
      let actionType: ActionItem['actionType'] = 'renew';
      let title = doc.action_label || 'Document à renouveler';
      
      if (dateContext === 'event') {
        actionType = 'verify';
        title = doc.action_label || 'Événement à venir';
      } else if (dateContext === 'deadline') {
        actionType = 'pay';
        title = doc.action_label || 'À utiliser avant expiration';
      } else if (dateContext === 'end_commitment') {
        actionType = 'cancel';
        title = doc.action_label || 'Fin d\'engagement';
      }

      allItems.push({
        id: doc.id,
        type: 'expiring',
        actionType,
        title,
        documentTitle: doc.title,
        documentId: doc.id,
        docType: doc.doc_type,
        priority: days < 0 ? 'urgent' : days <= 30 ? 'high' : 'medium',
        expiresAt: doc.expires_at!,
        daysUntilExpiry: days,
        filePath: doc.file_path,
        mimeType: doc.mime_type,
        linkedDemarche: docToDemarche.get(doc.id),
        dateContext,
        actionRequired: doc.action_required as string | null,
        score: days <= 30 ? SCORE_WEIGHTS.expiration_30d : SCORE_WEIGHTS.expiration_90d,
      });
    });

    // Transform FRESHNESS (administrative validity)
    (freshnessDocsData || []).filter(doc => !seenDocIds.has(doc.id)).forEach(doc => {
      const days = differenceInDays(new Date(doc.fresh_until!), today);
      seenDocIds.add(doc.id);

      allItems.push({
        id: doc.id,
        type: 'expiring',
        actionType: 'renew',
        title: 'Justificatif à rafraîchir',
        documentTitle: doc.title,
        documentId: doc.id,
        docType: doc.doc_type,
        priority: days <= 15 ? 'high' : 'medium',
        expiresAt: doc.fresh_until!,
        daysUntilExpiry: days,
        filePath: doc.file_path,
        mimeType: doc.mime_type,
        isFreshness: true, // Flag for different UI treatment
        score: days <= 15 ? SCORE_WEIGHTS.freshness_15d : SCORE_WEIGHTS.freshness_30d,
      });
    });

    // Transform MANUAL ACTIONS (user-created tasks)
    (manualActionsData || []).forEach(action => {
      const isManual = !action.document_id;
      const days = action.due_date 
        ? differenceInDays(new Date(action.due_date), today)
        : undefined;

      allItems.push({
        id: action.id,
        type: 'action',
        actionType: action.action_type as ActionItem['actionType'],
        title: (action as any).custom_title || action.label,
        documentTitle: isManual ? undefined : action.document?.title,
        documentId: action.document_id || action.id,
        docType: isManual ? (action as any).target_doc_type : action.document?.doc_type,
        priority: action.priority as ActionItem['priority'],
        dueDate: action.due_date || undefined,
        filePath: isManual ? undefined : action.document?.file_path,
        mimeType: isManual ? undefined : action.document?.mime_type,
        daysUntilExpiry: days,
        isManual,
        score: 50, // Lower than critical sources
      });
    });

    // Sort by score (highest first), then by days until expiry
    allItems.sort((a, b) => {
      const scoreA = (a as any).score || 0;
      const scoreB = (b as any).score || 0;
      if (scoreB !== scoreA) return scoreB - scoreA;
      
      const daysA = a.daysUntilExpiry ?? 999;
      const daysB = b.daysUntilExpiry ?? 999;
      return daysA - daysB;
    });

    setItems(allItems);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate stats
  const stats = useMemo<ActionStats>(() => {
    const expired = items.filter(i => (i.daysUntilExpiry ?? 999) < 0).length;
    const urgent = items.filter(i => i.priority === 'urgent' || i.priority === 'high').length;
    const thisWeek = items.filter(i => {
      const days = i.daysUntilExpiry ?? 999;
      return days >= 0 && days <= 7;
    }).length;

    return {
      total: items.length,
      completed: completedThisMonth,
      urgent,
      thisWeek,
      expired
    };
  }, [items, completedThisMonth]);

  return {
    items,
    stats,
    loading,
    completedThisMonth,
    refetch: fetchData,
  };
}

// Helper: Get the next relevant deadline for a fine
function getNextFineDeadline(fine: any): string | null {
  const today = new Date();
  const deadlines = [
    { date: fine.reduced_deadline, type: 'reduced' },
    { date: fine.payment_deadline, type: 'normal' },
  ].filter(d => d.date && new Date(d.date) >= today);

  if (deadlines.length > 0) {
    return deadlines.sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    )[0].date;
  }

  // If all deadlines passed, return the payment_deadline to show as overdue
  return fine.payment_deadline;
}
